import React, { useState, useRef, useEffect } from 'react';
import { MdMyLocation, MdPhone, MdOutlineEmail, MdAttachMoney, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { OptionCard } from '../ui/OptionCard';
import { registerFromQuote, autoLogin } from '../services/AccountService';
import { formatCurrency } from '../util/formatters';
import Session from '../auth/Session';
import { SessionExpiredError } from '../services/apiClient';
import GoogleAutoComplete from '../ui/GoogleAutoComplete';

// Owner type options
const OWNER_TYPES = [
    { value: 'INDIVIDUAL', label: 'Individual' },
    { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
    { value: 'LIMITED_LIABILITY_COMPANY', label: 'LLC' },
    { value: 'PARTNERSHIP', label: 'Partnership' },
    { value: 'CORPORATION', label: 'Corporation' },
    { value: 'S_CORPORATION', label: 'S-Corporation' },
    { value: 'TRUST', label: 'Trust' },
];

// Property exposure checkboxes
const EXPOSURE_ITEMS = [
    { field: 'ExposedHome', label: 'Home' },
    { field: 'ExposedLandImprovements', label: 'Land/Improvements (sea walls, retaining walls, docks, etc)' },
    { field: 'ExposedHardscaping', label: 'Hardscaping and landscaping' },
    { field: 'ExposedContents', label: 'Contents' },
    { field: 'ExposedValuables', label: 'Valuables (art, collections, etc.)' },
    { field: 'ExposedVehicles', label: 'Vehicles (cars, golf carts, etc)' },
    { field: 'ExposedLivingExpenses', label: 'Living expenses if there is damage (housing, food, etc)' },
];

const MIN_COVERAGE = 10000;
const MAX_COVERAGE = 10000000;
const COVERAGE_STEP = 5000;
const DEFAULT_COVERAGE = 20000;

export function CheckEligibility({ history }) {
    const { formData, setField, setFields, setStep } = useFormContext();
    const { isReadOnly, readOnlyProps } = useReadOnly();
    const formRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [displayAddress, setDisplayAddress] = useState(formData.TargetAddressFormatted || '');

    const coverageInputRef = useRef(formData.CoverageAmount?.toString() || '');
    const [coverageInputValue, setCoverageInputValue] = useState(formData.CoverageAmount?.toString() || '');

    const isLoggedIn = Session.isTokenValid();

    const roles = Session.getRole() || '';
    const isAdminCSR = roles.includes('Admin') || roles.includes('CSR');

    useEffect(() => {
        setStep('eligibility');
    }, []);

    useEffect(() => {
        if (isAdminCSR) return;
        const userName = Session.getUserName();
        setField('UserName', userName)
    }, [isAdminCSR])

    // Field helpers
    const handleField = (field) => (e) => setField(field, e.target.value);
    const handleCheck = (field) => (e) => setField(field, e.target.checked);

    // Google Autocomplete
    const handleAddressChange = (value) => {
        setDisplayAddress(value);
        // Clear coordinates so stale lat/lng from a previous selection
        // can't sneak through validation if the user types a new address
        // without selecting from the autocomplete dropdown
        setFields({
            TargetAddressOne: value,
            TargetLat: null,
            TargetLng: null,
        });
    }

    const handlePlaceSelected = (place) => {
        if (!place?.address_components) return;
        const comp = place.address_components;
        const get = (type) => comp.find((c) => c.types.includes(type)) || {};

        const streetNumber = get('street_number').long_name || '';
        const route = get('route').long_name || '';
        const city = get('locality').long_name || get('sublocality').long_name || '';
        const state = get('administrative_area_level_1').short_name || '';
        const zip = get('postal_code').long_name || '';
        const sub = get('subpremise').long_name || '';

        const payload = {
            TargetAddressOne: `${streetNumber} ${route}`.trim(),
            TargetAddressTwo: sub,
            TargetCity: city,
            TargetState: state,
            TargetZipCode: zip,
            TargetLat: place.geometry?.location?.lat() ?? null,
            TargetLng: place.geometry?.location?.lng() ?? null,
            TargetAddressFormatted: place.formatted_address
        };

        // Show the full formatted address in the input for clarity
        setDisplayAddress(place.formatted_address || '');
        setField('TargetAddressFormatted', place.formatted_address);
        setFields(payload);
    };

    // Coverage slider
    const handleCoverageSlider = (e) => {
        const val = Number(e.target.value);
        coverageInputRef.current = val.toString();
        setField('CoverageAmount', val);
        setCoverageInputValue(val.toString());
    };

    const handleCoverageInput = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, '');
        coverageInputRef.current = val;
        setField('CoverageAmount', val === '' ? '' : Number(val));
        setCoverageInputValue(val);
    };

    const handleCoverageBlur = () => {
        const val = Number(coverageInputValue);
        const clamped = isNaN(val) || val < MIN_COVERAGE
            ? MIN_COVERAGE
            : val > MAX_COVERAGE
            ? MAX_COVERAGE
            : val;
        coverageInputRef.current = clamped.toString();
        setCoverageInputValue(clamped.toString());
        setField('CoverageAmount', clamped);
    };

    // Validation
    const validate = (coverage = Number(formData.CoverageAmount)) => {
        const e = {};
        if (!formData.TargetAddressOne?.trim()) e.TargetAddressOne = 'Property address is required.';
        if (!formData.TargetLat || !formData.TargetLng) e.TargetAddressOne = 'Please select an address from the suggestions.';
        if (formData.IsExposedValueOverMillion === null || formData.IsExposedValueOverMillion === undefined)
            e.IsExposedValueOverMillion = 'Please answer this question.';
        if (formData.IsExposedValueOverMillion === false)
            e.IsExposedValueOverMillion = 'A minimum exposed property value of $1,000,000 is required for coverage.';
        if (!formData.FirstName?.trim()) e.FirstName = 'First name is required.';
        if (!formData.LastName?.trim()) e.LastName = 'Last name is required.';
        if (!formData.UserName?.trim()) e.UserName = 'Email address is required.';
        if (formData.UserName && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.UserName))
            e.UserName = 'Please enter a valid email address.';
        if (!formData.PhoneNumberPrimary?.trim()) e.PhoneNumberPrimary = 'Phone number is required.';
        if (!formData.OwnerType) e.OwnerType = 'Please select a property owner type.';
        if (!coverage || coverage < MIN_COVERAGE || coverage > MAX_COVERAGE)
            e.CoverageAmount = `Please select a coverage amount between ${formatCurrency(MIN_COVERAGE, '-', 0)} and ${formatCurrency(MAX_COVERAGE, '-', 0)}.`;
        return e;
    };

    const scrollToFirstError = () => {
        if (!formRef.current) return;
        const allErrors = formRef.current.querySelectorAll('.field-error');
        if (allErrors.length > 0) {
            allErrors[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Submit
    const handleSubmit = async () => {
        if (isReadOnly) {
            history.push('/signup/data-points');
            return;
        }
    
        const currentCoverage = Number(coverageInputRef.current);
        setField('CoverageAmount', currentCoverage);
    
        const errs = validate(currentCoverage);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            setTimeout(scrollToFirstError, 50);
            return;
        }
        setErrors({});
        setSubmitting(true);
    
        // Account creation + auto-login
        // Only attempt if the user is not already logged in
        const alreadyLoggedIn = Session.isTokenValid();
    
        if (!alreadyLoggedIn) {
            try {
                const result = await registerFromQuote({
                    email: formData.UserName,
                    firstName: formData.FirstName,
                    lastName: formData.LastName,
                    phone: formData.PhoneNumberPrimary,
                });
    
                if (result.accountExists) {
                    // Account already exists - redirect to login with message
                    history.push('/login', {
                        message: 'An account already exists for this email address. Please log in to continue.'
                    });
                    return;
                }
    
                if (!result.success) {
                    setErrors({ api: result.errorMessage || 'Account creation failed. Please try again.' });
                    setSubmitting(false);
                    return;
                }
    
                // Auto-login with temp password
                const loggedIn = await autoLogin(formData.UserName, result.tempPassword);
                if (!loggedIn) {
                    setErrors({ api: 'Account created but login failed. Please try logging in manually.' });
                    setSubmitting(false);
                    return;
                }
    
            } catch (err) {
                if (err instanceof SessionExpiredError) return;
                setErrors({ api: 'Something went wrong. Please try again.' });
                setSubmitting(false);
                return;
            }
        }
    
        // Navigate to next step
        history.push('/signup/data-points');
    };

    const sliderPct = ((formData.CoverageAmount - MIN_COVERAGE) / (MAX_COVERAGE - MIN_COVERAGE)) * 100;

    return (
        <SignupLayout currentStep={1} totalSteps={3} stepLabel="Check eligibility">
            <div ref={formRef}>

                {/* Page heading */}
                <h1 className="text-h1" style={{marginBottom: `var(--space-lg)`}}>Check coverage eligibility</h1>

                {/* SECTION 1 - Property details */}
                <section className="signup-section">
                    <h2 className="text-h4--brand">Property details</h2>
                    <p className="text-default">
                        Tell us about your property to see if it qualifies for parametric hurricane coverage.
                    </p>

                    {/* Address */}
                    <div className="form-group">
                        <p className="text-label" style={{marginTop: `var(--space-lg)`}}><strong>Where is the property located?</strong></p>
                        <p className="text-small" style={{marginBottom: '0px'}}>Start typing the property address and we'll find it for you.</p>
                        <div className="input-icon-wrap">
                            <span className="input-icon"><MdMyLocation size={16} /></span>
                            <GoogleAutoComplete
                                value={displayAddress}
                                onChange={handleAddressChange}
                                onPlaceSelected={handlePlaceSelected}
                                placeholder="Search for a property address"
                                style={{ paddingLeft: '36px' }}
                                {...readOnlyProps}
                            />
                        </div>
                        {errors.TargetAddressOne && (
                            <span className="error-text field-error">{errors.TargetAddressOne}</span>
                        )}
                    </div>

                    {/* Exposure checklist */}
                    <div className="form-group">
                        <p className="text-label" style={{marginTop: `var(--space-lg)`}}>
                            <strong>Tell us more about your property.</strong>
                        </p>
                        <p className="text-small" style={{marginBottom: `var(--space-2xs)`}}>
                            In the event of a hurricane, do you have any of the following that could be impacted? (Select all that apply)
                        </p>
                        <div className="checklist">
                            {EXPOSURE_ITEMS.map(({ field, label }) => (
                                <label key={field} className="checklist__item" {...readOnlyProps}>
                                    <input
                                        type="checkbox"
                                        checked={!!formData[field]}
                                        onChange={handleCheck(field)}
                                        className="checklist__input" 
                                        {...readOnlyProps}                                   
                                    />
                                    <span className="checklist__box">
                                        {formData[field] && (
                                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="text-small">{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Exposed value question */}
                    <div className="form-group">
                        <p className="text-label" style={{marginTop: `var(--space-lg)`}}>
                            <strong>Based on the checklist above, if a loss occurred, would your total exposed property value be $1MM or more?</strong>
                        </p>
                        <div className="yn-row">
                            <OptionCard
                                selected={formData.IsExposedValueOverMillion === true}
                                onChange={() => setField('IsExposedValueOverMillion', true)}
                                {...readOnlyProps}
                            >
                                <span>Yes</span>
                            </OptionCard>
                            <OptionCard
                                selected={formData.IsExposedValueOverMillion === false}
                                onChange={() => setField('IsExposedValueOverMillion', false)}
                                {...readOnlyProps}
                            >
                                <span>No</span>
                            </OptionCard>
                        </div>
                        {errors.IsExposedValueOverMillion && (
                            <span className="error-text field-error">{errors.IsExposedValueOverMillion}</span>
                        )}
                    </div>
                </section>

                {/* SECTION 2 - Your details */}
                <section className="signup-section" style={{marginTop: `var(--space-lg)`}}>
                    <h2 className="text-h4--brand">Your details</h2>
                    <p className="text-default">
                        Make sure your contact information is accurate as this is how we'll reach you.
                    </p>

                    {/* Full name */}
                    <div className="form-group" style={{marginTop: `var(--space-lg)`}}>
                        <p className="text-label"><strong>Provide your full name.</strong></p>
                        <input
                            type="text"
                            placeholder="First name"
                            value={formData.FirstName || ''}
                            onChange={handleField('FirstName')}
                            {...readOnlyProps}
                        />
                        {errors.FirstName && <span className="error-text field-error">{errors.FirstName}</span>}
                        <input
                            type="text"
                            placeholder="Last name"
                            value={formData.LastName || ''}
                            onChange={handleField('LastName')}
                            {...readOnlyProps}
                        />
                        {errors.LastName && <span className="error-text field-error">{errors.LastName}</span>}
                    </div>

                    {/* Email */}
                    {(!isLoggedIn || isAdminCSR) && (
                        <div className="form-group" style={{marginTop: `var(--space-lg)`}}>
                            <p className="text-label"><strong>Enter your email address.</strong></p>
                            <p className="text-small">This will be your account login and where we send your policy documents.</p>
                            <div className="input-icon-wrap" style={{marginTop: 0}}>
                                <span className="input-icon"><MdOutlineEmail size={16} /></span>
                                <input
                                    type="email"
                                    placeholder="myemail@email.com"
                                    value={formData.UserName || ''}
                                    onChange={handleField('UserName')}
                                    style={{ paddingLeft: '36px' }}
                                    {...readOnlyProps}
                                />
                            </div>
                            {errors.UserName && <span className="error-text field-error">{errors.UserName}</span>}

                            {/* Privacy opt-in */}
                            <label className="checklist__item" style={{ marginTop: 'var(--space-sm)' }} {...readOnlyProps}>
                                <input
                                    type="checkbox"
                                    checked={!!formData.IsReceivingCriticalMessages}
                                    onChange={handleCheck('IsReceivingCriticalMessages')}
                                    className="checklist__input"
                                    {...readOnlyProps}
                                />
                                <span className="checklist__box">
                                    {formData.IsReceivingCriticalMessages && (
                                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </span>
                                <span className="checklist__label small">
                                    I agree to receive updates from Elevana in accordance with our{' '}
                                    <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Phone */}
                    <div className="form-group" style={{marginTop: `var(--space-lg)`}}>
                        <p className="text-label"><strong>Provide your phone number.</strong></p>
                        <div className="input-icon-wrap">
                            <span className="input-icon"><MdPhone size={16} /></span>
                            <input
                                type="tel"
                                placeholder="555-555-5555"
                                value={formData.PhoneNumberPrimary || ''}
                                onChange={handleField('PhoneNumberPrimary')}
                                style={{ paddingLeft: '36px' }}
                                {...readOnlyProps}
                            />
                        </div>
                        {errors.PhoneNumberPrimary && (
                            <span className="error-text field-error">{errors.PhoneNumberPrimary}</span>
                        )}
                    </div>

                    {/* Owner type */}
                    <div className="form-group" style={{marginTop: `var(--space-lg)`}}>
                        <p className="text-label"><strong>Select the property owner type.</strong></p>
                        <select
                            value={formData.OwnerType || 'INDIVIDUAL'}
                            onChange={handleField('OwnerType')}
                            {...readOnlyProps}
                        >
                            {OWNER_TYPES.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {errors.OwnerType && <span className="error-text field-error">{errors.OwnerType}</span>}
                    </div>
                </section>

                {/* SECTION 3 - Select your coverage */}
                <section className="signup-section">
                    <h2 className="text-h4--brand">Select your coverage</h2>
                    <p className="text-default" style={{marginBottom: `var(--space-lg)`}}>
                        Use the slider to choose a coverage amount or enter an exact value below.
                    </p>

                    {/* Coverage display chip */}
                    <div className="coverage-chip">
                        <span className="coverage-chip__amount">{formatCurrency(formData.CoverageAmount || MIN_COVERAGE, '-', 0)}</span>
                        <span className="coverage-chip__label">Coverage amount</span>
                    </div>

                    {/* Slider */}
                    <div className="coverage-slider-wrap">
                        <input
                            type="range"
                            min={MIN_COVERAGE}
                            max={MAX_COVERAGE}
                            step={COVERAGE_STEP}
                            value={formData.CoverageAmount || DEFAULT_COVERAGE}
                            onChange={handleCoverageSlider}
                            className="coverage-slider"
                            style={{ '--slider-pct': `${sliderPct}%` }}
                            {...readOnlyProps}
                        />
                        <div className="coverage-slider__labels">
                            <span>{formatCurrency(MIN_COVERAGE, '-', 0)}</span>
                            <span>$10MM</span>
                        </div>
                    </div>

                    {/* Exact value input */}
                    <div className="form-group" style={{marginTop: `var(--space-lg)`}}>
                        <p className="text-label"><strong>Enter an exact coverage value.</strong></p>
                        <div className="input-icon-wrap">
                            <span className="input-icon"><MdAttachMoney size={16} /></span>
                            <input
                                type="number"
                                min={MIN_COVERAGE}
                                max={MAX_COVERAGE}
                                step={COVERAGE_STEP}
                                value={coverageInputValue}
                                onChange={handleCoverageInput}
                                onBlur={handleCoverageBlur}
                                style={{ paddingLeft: '36px' }}
                                {...readOnlyProps}
                            />
                        </div>
                        {errors.CoverageAmount && (
                            <span className="error-text field-error">{errors.CoverageAmount}</span>
                        )}
                    </div>
                </section>

                {/* Errors from account creation */}
                {errors.api && (
                    <p className="error-text" style={{ textAlign: 'center', marginBottom: 'var(--space-md)' }}>
                        {errors.api}
                    </p>
                )}

                {/* CTA */}
                <button
                    className="btn signup-cta"
                    onMouseDown={(e) => {
                        e.preventDefault(); // prevents input blur from firing before submit
                        handleSubmit();
                    }}
                    disabled={submitting}
                >
                    {submitting ? ('Checking…') : (<>Check eligibility <MdArrowForward /></>)}
                </button>
            </div>
        </SignupLayout>
    );
}